import {Button, toast} from '@cashiaApp/web-components';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
} from 'react';
import {v4 as uuidv4} from 'uuid';

import {
  cardPayment,
  CardPaymentRequest,
  txPolling,
  TxStatusRequest,
  TxStatusResponse,
  mpesaPayment,
  MpesaPaymentRequest,
  TransactionResponse,
} from './api';
import BillingAddressForm, {
  BillingInfo,
  BillingAddressFormRef,
} from './components/BillingAddressForm';
import {CardInfo} from './components/CardPaymentForm';
import CardPaymentInline from './components/CardPaymentInline';
import {CashiaInfo} from './components/CashiaWalletForm';
import Footer from './components/Footer';
import Header from './components/Header';
import {MPesaInfo} from './components/MpesaPaymentForm';
import PaymentFailure from './components/PaymentFailure';
import PaymentMethodSelector, {
  PaymentMethod,
  PaymentMethodSelectorRef,
} from './components/PaymentSelector';
import PaymentSuccess from './components/PaymentSuccess';
import ProcessingPayment from './components/ProcessingPayment';
import Info from '../../../assets/icons/info-circle.svg';
import {CheckoutBaseFields} from '../../../components/CheckoutBaseFields';
import {CredentialsFields} from '../../../components/CredentialsFields';
import Spinner from '../../../components/tailwind/Spinner';
import {useCredentialsV2} from '../../../hooks/hmac/useHmacV2';
import {environment} from '../../../utils/constants';
import {generateCashiaHeadersV2} from '../../../utils/hmac/cashiaAuthV2';
import {cn} from '../../../utils/reusableFunctions';

interface BaseFormValues {
  terminalId: string;
  amount: number;
  currency: string;
  requestId: string;
  secretKey: string;
  keyId: string;
}

interface FormErrors {
  [key: string]: string;
}

const HostPageV2: React.FC = () => {
  const {showPassword, setShowPassword, saveCredentials, getCredentials} =
    useCredentialsV2();
  const storedCreds = getCredentials();
  const [paymentStatus, setPaymentStatus] = useState<
    'form' | 'processing' | 'success' | 'failed'
  >('form');
  const [baseValues, setBaseValues] = useState<BaseFormValues>({
    requestId: uuidv4(),
    terminalId: storedCreds?.terminalId || 'test',
    amount: 1000,
    currency: 'KES',
    secretKey: storedCreds?.secretKey || 'test',
    keyId: storedCreds?.keyId || 'test',
  });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [isSubmiitting, setIsSubmitting] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  // Billing info - no longer need billing errors state
  const [billingInfo, setBillingInfo] = useState<BillingInfo>(() =>
    environment == 'dev'
      ? {
          country: 'Kenya',
          locality: 'Nairobi',
          postalCode: '00101',
          address: 'Riverside Drive',
        }
      : {
          country: '',
          locality: '',
          postalCode: '',
          address: '',
        }
  );
  // Simplified state - forms manage their own errors now
  const [cardInfo, setCardInfo] = useState<CardInfo>(() =>
    environment == 'dev'
      ? {
          firstName: 'John',
          lastName: 'Doe',
          email: 'johndoe@gmail.com',
          cardNumber: '4000000000002503',
          expiryDate: '02/28',
          cvv: '123',
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          cardNumber: '',
          expiryDate: '',
          cvv: '',
        }
  );

  const [mpesaInfo, setMPesaInfo] = useState<MPesaInfo>({} as MPesaInfo);

  const [cashiaInfo, setCashiaInfo] = useState<CashiaInfo>({} as CashiaInfo);

  const [transactionResponse, setTransactionResponse] =
    useState<TransactionResponse | null>(null);

  // New polling-related state
  const [isPollingActive, setIsPollingActive] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to access form validation
  const paymentMethodRef = useRef<PaymentMethodSelectorRef>(null);
  const billingFormRef = useRef<BillingAddressFormRef>(null);

  const processTxStatusResponse = useCallback((response: TxStatusResponse) => {
    if (response?.transaction?.status === 'PROCESSED') {
      setShowCardModal(false);
      setPaymentStatus('success');
    } else if (response?.transaction?.status !== 'PROCESSED') {
      setShowCardModal(false);
      setPaymentStatus('failed');
    }
  }, []);

  // Enhanced polling effect
  useEffect(() => {
    // Don't start polling if no transaction
    if (transactionResponse == null) {
      return;
    }

    setIsPollingActive(true);

    const fetchData = async () => {
      try {
        const requestBody: TxStatusRequest = {
          txId: transactionResponse.transaction?.id || '',
        };
        const headers = generateCashiaHeadersV2({
          secret: baseValues.secretKey,
          keyId: baseValues.keyId,
          requestBody: requestBody,
          method: 'POST',
        });
        const response = await txPolling(requestBody, headers);
        // Check if transaction is still pending
        if (
          response?.transaction?.status == 'PENDING' ||
          response?.transaction?.status == 'PROVIDER_ASSIGNED' ||
          response?.transaction?.status == 'PROVIDER_PROCESSED'
        ) {
          // Still pending, continue polling
          return;
        }
        // Transaction completed (success, failed, etc.)
        setIsPollingActive(false);
        // Update payment status based on response
        processTxStatusResponse(response);

        // Clear the polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } catch (error) {
        // setIsPollingActive(false);
        // setPaymentStatus('failed');
        // // Clear the polling interval on error
        // if (pollingIntervalRef.current) {
        //   clearInterval(pollingIntervalRef.current);
        //   pollingIntervalRef.current = null;
        // }
      }
    };

    // Fetch immediately on start
    void fetchData();

    // Set up interval polling
    pollingIntervalRef.current = setInterval(() => {
      void fetchData();
    }, 2000);

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPollingActive(false);
    };
  }, [
    processTxStatusResponse,
    transactionResponse,
    baseValues.keyId,
    baseValues.secretKey,
  ]);

  // Cleanup polling when component unmounts or payment status changes
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (paymentMethod !== 'card') {
      setShowBilling(false); // hide billing info if switching from card
    }
  }, [paymentMethod]);

  const handleCardSubmit = async () => {
    // Validate payment method using ref with proper type checking
    let isPaymentMethodValid = false;
    if (paymentMethodRef.current) {
      isPaymentMethodValid = paymentMethodRef.current.validateCurrentMethod();
    }
    // Validate billing if shown using ref with proper type checking
    let isBillingValid = true;
    if (showBilling && billingFormRef.current) {
      isBillingValid = billingFormRef.current.validateAll();
    }
    if (!isPaymentMethodValid || !isBillingValid) {
      // Validation failed, errors are already shown in the forms
      return;
    }
    setIsSubmitting(true);
    const cardPaymentRequest: CardPaymentRequest = {
      requestId: baseValues.requestId,
      terminalId: baseValues.terminalId,
      currency: baseValues.currency,
      amount: baseValues.amount,
      cardInfo: {
        cardNumber: cardInfo.cardNumber.replace(/\s/g, ''),
        cardExpirationMonth: cardInfo?.expiryDate?.slice(0, 2) || '00',
        cardExpirationYear: `${
          parseInt(cardInfo?.expiryDate?.slice(3, 5) || '', 10) + 2000
        }`,
        cardCvv: cardInfo.cvv,
      },
    };
    if (showBilling) {
      cardPaymentRequest.billingInfo = {
        firstName: cardInfo.firstName,
        lastName: cardInfo.lastName,
        country: billingInfo.country,
        locality: billingInfo.locality,
        postalCode: billingInfo.postalCode,
        address1: billingInfo.address,
        email: cardInfo.email,
        // phoneNumber: '', // Phone number can be added if collected
      };
    }

    try {
      const headers = generateCashiaHeadersV2({
        secret: baseValues.secretKey,
        keyId: baseValues.keyId,
        requestBody: cardPaymentRequest,
        method: 'POST',
      });
      const response = await cardPayment(cardPaymentRequest, headers);
      setTransactionResponse(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
      setShowCardModal(true);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleMpesaSubmit = async () => {
    // Validate payment method using ref with proper type checking
    let isValid = false;
    if (paymentMethodRef.current) {
      isValid = paymentMethodRef.current.validateCurrentMethod();
    }

    if (!isValid) {
      // Validation failed, errors are already shown in the form
      return;
    }

    setIsSubmitting(true);
    const mpesaPaymentRequest: MpesaPaymentRequest = {
      requestId: baseValues.requestId,
      terminalId: baseValues.terminalId,
      currency: baseValues.currency,
      amount: baseValues.amount,
      phoneNumber: mpesaInfo.phoneNumber.trim().replace(/\+/g, ''),
    };

    try {
      const headers = generateCashiaHeadersV2({
        secret: baseValues.secretKey,
        keyId: baseValues.keyId,
        requestBody: mpesaPaymentRequest,
        method: 'POST',
      });
      const response = await mpesaPayment(mpesaPaymentRequest, headers);
      setTransactionResponse(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCashiaSubmit = async () => {
    // Validate payment method using ref with proper type checking
    let isValid = false;
    if (paymentMethodRef.current) {
      isValid = paymentMethodRef.current.validateCurrentMethod();
    }

    if (!isValid) {
      // Validation failed, errors are already shown in the form
      return;
    }

    setIsSubmitting(true);
    // TODO: Implement Cashia payment API call
    // For now, just show a success message
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsSubmitting(false);
      setPaymentStatus('processing');
      toast.success('Payment request sent successfully');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (paymentMethod === 'card') {
      await handleCardSubmit();
    } else if (paymentMethod === 'mpesa') {
      await handleMpesaSubmit();
    } else if (paymentMethod === 'cashia') {
      await handleCashiaSubmit();
    }
  };

  const [errors] = useState<FormErrors>({});

  const renderErrorMessage = (fieldName: string) => {
    if (errors[fieldName]) {
      return (
        <span className="flex items-center gap-2">
          <img src={Info} alt="Error" />
          <span className="text-orangeDark px-4 rounded text-sm font-normal bg-lightOrange">
            {errors[fieldName]}
          </span>
        </span>
      );
    }
    return null;
  };

  const handleBaseValuesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setBaseValues({
      ...baseValues,
      [name]: value,
    });
  };

  useEffect(() => {
    // Save credentials whenever they change
    saveCredentials(
      baseValues.keyId,
      baseValues.secretKey,
      baseValues.terminalId
    );
  }, [
    baseValues.keyId,
    baseValues.secretKey,
    baseValues.terminalId,
    saveCredentials,
  ]);

  return (
    <div className="lg:flex min-h-screen  flex flex-col lg:flex-row">
      <div
        className={`lg:w-1/2 bg-transparent lg:bg-surfaceGrey lg:px-16 ${
          paymentStatus !== 'form' ? 'py-0 pt-8 lg:py-14' : 'py-14'
        }`}>
        <div className="px-3 md:px-12 mb-8">
          <Header />
        </div>

        <div className="flex flex-col lg:items-start items-center px-12">
          {/* <div className="mb-3 md:mb-8">
            <Timer
              minutes={timeRemaining.minutes}
              seconds={timeRemaining.seconds}
            />
          </div> */}
          <div className="lg:w-full text-center lg:text-start">
            {/* Base Checkout Fields */}
            <CredentialsFields
              terminalId={baseValues.terminalId}
              keyId={baseValues.keyId}
              secretKey={baseValues.secretKey}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onChange={handleBaseValuesChange}
              renderErrorMessage={renderErrorMessage}
            />
            {/* Base Checkout Fields */}
            <CheckoutBaseFields
              requestId={baseValues.requestId}
              amount={baseValues.amount}
              currency={baseValues.currency}
              onChange={handleBaseValuesChange}
              renderErrorMessage={renderErrorMessage}
            />
          </div>
        </div>
      </div>
      <div className="lg:w-1/2 bg-transparent xl:px-16 lg:py-14 lg:shadow-[0px_16px_32px_4px_rgba(88,80,98,0.10)] flex-1 flex">
        {paymentStatus === 'form' && (
          <div className="flex flex-col flex-1 justify-center px-4 lg:px-16 items-start w-full">
            <>
              {showBilling && (
                <BillingAddressForm
                  ref={billingFormRef}
                  billingInfo={billingInfo}
                  setBillingInfo={setBillingInfo}
                />
              )}
              <PaymentMethodSelector
                terminalId={baseValues.terminalId}
                keyId={baseValues.keyId}
                secretKey={baseValues.secretKey}
                amount={baseValues.amount}
                currency={baseValues.currency}
                ref={paymentMethodRef}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                cardInfo={cardInfo}
                setCardInfo={setCardInfo}
                mpesaInfo={mpesaInfo}
                setMPesaInfo={setMPesaInfo}
                cashiaInfo={cashiaInfo}
                setCashiaInfo={setCashiaInfo}
                onBinCheck={setShowBilling}
              />
              <div className="w-full mb-8 mt-8">
                <Button
                  onClick={handleSubmit}
                  className={cn(
                    'p-3 w-full bg-pink mt-3 active:scale-95 mt-0',
                    {
                      'text-white bg-greyish cursor-default': isSubmiitting,
                    }
                  )}
                  disabled={isSubmiitting || isPollingActive}>
                  {isSubmiitting ? (
                    <Spinner className="fill-white" />
                  ) : (
                    <p className="font-[600]">Pay</p>
                  )}
                </Button>
              </div>
              <div className="pb-4 flex flex-col items-center w-full flex-1 justify-end lg:justify-start">
                <Footer />
              </div>
            </>
          </div>
        )}
        {paymentStatus !== 'form' && (
          <>
            {paymentStatus === 'processing' && showCardModal && (
              <CardPaymentInline
                keyId={baseValues.keyId}
                secretKey={baseValues.secretKey}
                isVisible={showCardModal}
                transactionResponse={transactionResponse}
              />
            )}
            {paymentStatus === 'processing' && paymentMethod === 'mpesa' && (
              <ProcessingPayment />
            )}
            {paymentStatus === 'success' && <PaymentSuccess />}
            {paymentStatus === 'failed' && (
              <PaymentFailure transactionResponse={transactionResponse} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HostPageV2;
